import { create } from "zustand";

type SelectionValue = string | null;

interface SelectionState {
  selectedBody: SelectionValue;
  /** Full list of selected body UUIDs. Length 0 = nothing, 1 = single-select, >1 = multi-select.
   *  Always kept in sync with selectedBody (which is null when size > 1). */
  selectedBodies: string[];
  selectedMesh: SelectionValue;
  selectedJoint: SelectionValue;
  selectedActuator: SelectionValue;
  selectedKeyframe: SelectionValue;
  selectedSite: SelectionValue;
  selectedGeom: SelectionValue;
  selectedInertial: SelectionValue;
  selectedEquality: SelectionValue;
  selectedTendon: SelectionValue;
  selectedCamera: SelectionValue;
  selectedLight: SelectionValue;
  focusModeActive: boolean;
  focusBodyName: SelectionValue;
  focusMeshUuid: SelectionValue;
  /** When set with focus mode, dims all geometry except this collision geom (scene `userData.geomUuid`). */
  focusCollisionGeomUuid: SelectionValue;
  lastSelectionSource: SelectionValue;
  selectionSequence: number;
  hoveredBody: SelectionValue;
  hoveredMesh: SelectionValue;
  hoveredJoint: SelectionValue;
  hoveredSite: SelectionValue;
  hoveredGeom: SelectionValue;
  hoveredInertial: SelectionValue;
  /** One-shot signal: which actuator row the accordion should auto-expand on next render. */
  expandedActuatorUuid: SelectionValue;
}

interface SelectionActions {
  selectBody: (bodyUuid: SelectionValue) => void; // Changed to UUID
  /** Ctrl/Cmd+click: add or remove a body from the multi-selection. */
  toggleBodyInSelection: (bodyUuid: string) => void;
  /** Programmatically replace the entire body selection set. */
  setSelectedBodies: (bodyUuids: string[]) => void;
  selectMesh: (meshUuid: SelectionValue) => void;
  selectJoint: (jointUuid: SelectionValue) => void; // Changed to UUID
  selectActuator: (actuatorUuid: SelectionValue) => void;
  selectSite: (siteUuid: SelectionValue) => void; // Changed to UUID
  selectGeom: (geomKey: SelectionValue) => void;
  selectInertial: (inertialKey: SelectionValue) => void;
  selectEquality: (equalityUuid: SelectionValue) => void;
  selectTendon: (tendonUuid: SelectionValue) => void;
  selectCamera: (cameraUuid: SelectionValue) => void;
  selectLight: (lightUuid: SelectionValue) => void;
  selectKeyframe: (keyframeName: SelectionValue) => void;
  setFocusMode: (
    active: boolean,
    bodyUuid?: SelectionValue,
    meshUuid?: SelectionValue,
    collisionGeomUuid?: SelectionValue
  ) => void;
  clearFocusMode: () => void;
  setHoveredBody: (bodyUuid: SelectionValue) => void; // Changed to UUID
  setHoveredMesh: (meshUuid: SelectionValue) => void;
  setHoveredJoint: (jointUuid: SelectionValue) => void; // Changed to UUID
  setHoveredSite: (siteUuid: SelectionValue) => void; // Changed to UUID
  setHoveredGeom: (geomKey: SelectionValue) => void;
  setHoveredInertial: (inertialKey: SelectionValue) => void;
  clearAllSelections: () => void;
  setLastSelectionSource: (source: SelectionValue) => void;
  clearAllHovers: () => void;
  setExpandedActuatorUuid: (uuid: SelectionValue) => void;
  selectActuatorInParent: (
    actuatorUuid: string,
    parentJointUuid: SelectionValue,
    parentTendonUuid: SelectionValue
  ) => void;
}

interface SelectionDerived {
  getEffectiveBody: () => SelectionValue;
  getEffectiveMesh: () => SelectionValue;
  getEffectiveJoint: () => SelectionValue;
  getEffectiveSite: () => SelectionValue;
  getEffectiveGeom: () => SelectionValue;
  getEffectiveInertial: () => SelectionValue;
  getEffectiveEquality: () => SelectionValue;
  getEffectiveTendon: () => SelectionValue;
}

export type SelectionStore = SelectionState & SelectionActions & SelectionDerived;

const normalizeBodySelection = (bodyUuids: readonly string[]) =>
  Array.from(new Set(bodyUuids.filter((uuid) => typeof uuid === "string" && uuid.length > 0)));

export const useSelectionStore = create<SelectionStore>((set, get) => ({
  selectedBody: null,
  selectedBodies: [],
  selectedMesh: null,
  selectedJoint: null,
  selectedActuator: null,
  selectedKeyframe: null,
  selectedSite: null,
  selectedGeom: null,
  selectedInertial: null,
  selectedEquality: null,
  selectedTendon: null,
  selectedCamera: null,
  selectedLight: null,
  focusModeActive: false,
  focusBodyName: null,
  focusMeshUuid: null,
  focusCollisionGeomUuid: null,
  lastSelectionSource: null,
  selectionSequence: 0,
  hoveredBody: null,
  hoveredMesh: null,
  hoveredJoint: null,
  hoveredSite: null,
  hoveredGeom: null,
  hoveredInertial: null,
  expandedActuatorUuid: null,

  selectBody: (bodyUuid) =>
    set((state) => {
      const next = bodyUuid === state.selectedBody ? null : bodyUuid;
      return {
        selectedBody: next,
        selectedBodies: next ? [next] : [],
        selectedMesh: null,
        selectedJoint: null,
        selectedActuator: null,
        selectedSite: null,
        selectedGeom: null,
        selectedInertial: null,
        selectedEquality: null,
        selectedTendon: null,
        selectedCamera: null,
        selectedLight: null,
      };
    }),

  toggleBodyInSelection: (bodyUuid) =>
    set((state) => {
      const next = new Set(state.selectedBodies);
      if (next.has(bodyUuid)) {
        next.delete(bodyUuid);
      } else {
        next.add(bodyUuid);
      }
      const single = next.size === 1 ? [...next][0] : null;
      return {
        selectedBodies: [...next],
        selectedBody: single,
        selectedMesh: null,
        selectedJoint: null,
        selectedActuator: null,
        selectedSite: null,
        selectedGeom: null,
        selectedInertial: null,
        selectedEquality: null,
        selectedTendon: null,
        selectedCamera: null,
        selectedLight: null,
      };
    }),

  setSelectedBodies: (bodyUuids) => {
    const next = normalizeBodySelection(bodyUuids);
    const single = next.length === 1 ? next[0] : null;
    set({
      selectedBodies: next,
      selectedBody: single,
      selectedMesh: null,
      selectedJoint: null,
      selectedActuator: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedCamera: null,
      selectedLight: null,
    });
  },

  selectMesh: (meshUuid) =>
    set((state) => ({
      selectedMesh: meshUuid === state.selectedMesh ? null : meshUuid,
      selectedBody: null,
      selectedBodies: [],
      selectedJoint: null,
      selectedActuator: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedCamera: null,
      selectedLight: null,
    })),

  selectJoint: (jointUuid) =>
    set((state) => ({
      selectedJoint: jointUuid === state.selectedJoint ? null : jointUuid,
      expandedActuatorUuid: null,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedActuator: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedCamera: null,
      selectedLight: null,
    })),

  selectActuator: (actuatorUuid) =>
    set((state) => ({
      selectedActuator: actuatorUuid === state.selectedActuator ? null : actuatorUuid,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedJoint: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedCamera: null,
      selectedLight: null,
    })),

  selectSite: (siteUuid) =>
    set((state) => ({
      selectedSite: siteUuid === state.selectedSite ? null : siteUuid,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedJoint: null,
      selectedActuator: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedCamera: null,
      selectedLight: null,
    })),

  selectGeom: (geomKey) =>
    set((state) => ({
      selectedGeom: geomKey === state.selectedGeom ? null : geomKey,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedJoint: null,
      selectedActuator: null,
      selectedSite: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedCamera: null,
      selectedLight: null,
    })),

  selectInertial: (inertialKey) =>
    set((state) => ({
      selectedInertial: inertialKey === state.selectedInertial ? null : inertialKey,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedJoint: null,
      selectedActuator: null,
      selectedSite: null,
      selectedGeom: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedCamera: null,
      selectedLight: null,
    })),

  selectEquality: (equalityUuid) =>
    set((state) => ({
      selectedEquality: equalityUuid === state.selectedEquality ? null : equalityUuid,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedJoint: null,
      selectedActuator: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedTendon: null,
      selectedCamera: null,
      selectedLight: null,
    })),

  selectTendon: (tendonUuid) =>
    set((state) => ({
      selectedTendon: tendonUuid === state.selectedTendon ? null : tendonUuid,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedJoint: null,
      selectedActuator: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedCamera: null,
      selectedLight: null,
    })),

  selectCamera: (cameraUuid) =>
    set((state) => ({
      selectedCamera: cameraUuid === state.selectedCamera ? null : cameraUuid,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedJoint: null,
      selectedActuator: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedLight: null,
    })),

  selectLight: (lightUuid) =>
    set((state) => ({
      selectedLight: lightUuid === state.selectedLight ? null : lightUuid,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedJoint: null,
      selectedActuator: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedCamera: null,
    })),

  selectKeyframe: (keyframeName) => set({ selectedKeyframe: keyframeName }),

  setFocusMode: (active, bodyUuid, meshUuid, collisionGeomUuid) =>
    set(() => ({
      focusModeActive: !!active,
      focusBodyName: active ? (bodyUuid ?? null) : null,
      focusMeshUuid: active ? (meshUuid ?? null) : null,
      focusCollisionGeomUuid: active ? (collisionGeomUuid ?? null) : null,
    })),

  clearFocusMode: () =>
    set({
      focusModeActive: false,
      focusBodyName: null,
      focusMeshUuid: null,
      focusCollisionGeomUuid: null,
    }),

  setHoveredBody: (bodyUuid) => set({ hoveredBody: bodyUuid }),
  setHoveredMesh: (meshUuid) => set({ hoveredMesh: meshUuid }),
  setHoveredJoint: (jointUuid) => set({ hoveredJoint: jointUuid }),
  setHoveredSite: (siteUuid) => set({ hoveredSite: siteUuid }),
  setHoveredGeom: (geomKey) => set({ hoveredGeom: geomKey }),
  setHoveredInertial: (inertialKey) => set({ hoveredInertial: inertialKey }),

  clearAllSelections: () =>
    set({
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedJoint: null,
      selectedActuator: null,
      selectedKeyframe: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedTendon: null,
      selectedCamera: null,
      selectedLight: null,
    }),

  setLastSelectionSource: (source) =>
    set((state) => ({
      lastSelectionSource: source ?? null,
      selectionSequence: state.selectionSequence + 1,
    })),

  setExpandedActuatorUuid: (uuid) => set({ expandedActuatorUuid: uuid ?? null }),

  selectActuatorInParent: (actuatorUuid, parentJointUuid, parentTendonUuid) =>
    set(() => ({
      expandedActuatorUuid: actuatorUuid,
      selectedJoint: parentJointUuid && !parentTendonUuid ? parentJointUuid : null,
      selectedTendon: parentTendonUuid ?? null,
      selectedBody: null,
      selectedBodies: [],
      selectedMesh: null,
      selectedActuator: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
      selectedEquality: null,
      selectedCamera: null,
      selectedLight: null,
    })),

  clearAllHovers: () =>
    set({
      hoveredBody: null,
      hoveredMesh: null,
      hoveredJoint: null,
      hoveredSite: null,
      hoveredGeom: null,
      hoveredInertial: null,
    }),

  getEffectiveBody: () => {
    const state = get();
    return state.hoveredBody || state.selectedBody;
  },

  getEffectiveMesh: () => {
    const state = get();
    return state.hoveredMesh || state.selectedMesh;
  },

  getEffectiveJoint: () => {
    const state = get();
    return state.hoveredJoint || state.selectedJoint;
  },

  getEffectiveSite: () => {
    const state = get();
    return state.hoveredSite || state.selectedSite;
  },

  getEffectiveGeom: () => {
    const state = get();
    return state.hoveredGeom || state.selectedGeom;
  },

  getEffectiveInertial: () => {
    const state = get();
    return state.hoveredInertial || state.selectedInertial;
  },

  getEffectiveEquality: () => {
    const state = get();
    return state.selectedEquality;
  },

  getEffectiveTendon: () => {
    const state = get();
    return state.selectedTendon;
  },
}));
